import { useState } from 'react';
import { X } from '@phosphor-icons/react';
import showToast from '@Common/toast/showToast';
import { analyzerAPI } from '@Lib/axios/analyzer';
import { AhaTicketGeneratorPrompt, promptAnalyzer } from '@Models/types';
import { useAuth } from '@Context/CognitoAuthProvider';
import { promptToolboxAPI } from '@Lib/axios/prompt-toolbox';
import { IoIosWarning } from 'react-icons/io';
interface ModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onPromptAdded?: (prompts: promptAnalyzer[]) => void;
  type?: string;
}

const AddPromptModal = ({
  isOpen,
  onCancel,
  onPromptAdded,
  type,
}: ModalProps) => {
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { logout } = useAuth();
  const analyzer = analyzerAPI(logout);
  const promptToolBox = promptToolboxAPI(logout);

  const handleSave = async () => {
    setError(null);
    if (!name.trim() || !content.trim()) {
      setError('Both name and content are required.');
      return;
    }
    const author = localStorage.getItem('email');
    if (!author) {
      setError('Author information missing. Please log in.');
      return;
    }
    setLoading(true);

    try {
      let updatedPrompts: promptAnalyzer[] | AhaTicketGeneratorPrompt[];
      if (type === 'generator prompt') {
        await promptToolBox.addAhaTicketGeneratorPrompt(name, content, author);
        updatedPrompts = await promptToolBox.getAhaTicketGeneratorPrompts();
      } else {
        await analyzer.addAnalyzerPrompt({
          id: crypto.randomUUID(),
          name,
          prompt: content,
          author,
        });
        updatedPrompts = await analyzer.getAnalyzerPrompts();
      }
      showToast.success('Prompt added successfully!');
      if (onPromptAdded) {
        onPromptAdded(updatedPrompts);
      }
      setName('');
      setContent('');
      onCancel();
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message || 'An error occurred.');
      } else {
        setError('An error occurred.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`${
        isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
      } fixed inset-0 z-50 flex h-full w-full items-center justify-center bg-gray-100 bg-opacity-70 transition-opacity duration-300`}
    >
      <div
        className={`mx-2 flex w-full transform flex-col rounded-xl shadow-xl transition-transform duration-300 md:w-1/2 ${
          isOpen ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}
      >
        <div className="flex h-12 items-center justify-between rounded-tl-lg rounded-tr-lg bg-primary-700 px-4 py-3">
          <p className="flex-[1.3] font-semibold text-white">Add Prompt</p>
          <div className="flex-1">
            <div className="flex flex-1 justify-end gap-2">
              <X
                onClick={onCancel}
                size={20}
                className="cursor-pointer text-white hover:text-red-500"
              />
            </div>
          </div>
        </div>
        <div className="flex h-80 flex-col gap-2 rounded-bl-lg rounded-br-lg bg-white p-4 font-inter text-primary-700">
          {error && (
            <div className="mb-2 rounded bg-red-200 p-2 text-sm text-red-700">
              {error}
            </div>
          )}
          <input
            className="w-full rounded-lg border-2 px-4 py-1 outline-primary-700 placeholder:italic placeholder:text-gray-400"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <textarea
            className="custom-scroll h-full w-full resize-none rounded-lg border-2 px-4 py-1 outline-primary-700 placeholder:italic placeholder:text-gray-400"
            placeholder="Content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
          <div className="flex flex-1 justify-end gap-2">
            <button
              onClick={onCancel}
              disabled={loading}
              className="flex w-10 items-center justify-center rounded-md bg-primary-100 px-10 py-1 text-center text-sm font-semibold text-primary-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex w-10 items-center justify-center rounded-md bg-primary-700 px-10 py-1 text-center text-sm font-semibold text-white"
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
        {type === 'generator prompt' ? (
          <div className="flex flex-col rounded-b-lg bg-white px-11 pb-5 text-red-600">
            <span className="flex flex-row items-center gap-1 text-xs font-bold sm:text-base">
              <IoIosWarning className="text-red-600" size="1rem" />
              Note:
            </span>
            <span className="mt-2 text-justify text-xs font-semibold sm:text-sm">
              The prompt you enter will be appended to a pre-determined
              instruction and format to ensure consistency in the generated
              feature ticket for the Aha! API. Please make sure to include the{' '}
              <span className="font-bold">{'{prompt} '}</span>
              placeholder where the feature description should go.
            </span>
          </div>
        ) : (
          <div></div>
        )}
      </div>
    </div>
  );
};

export default AddPromptModal;
